import { useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FieldValues, type UseFormSetError } from 'react-hook-form';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  ExternalContact,
  type AddExternalContactRequest,
  type AddExternalContactResponse,
  type UpdateExternalContactRequest
} from '@bliks-io/contracts';
import { useExternalContacts } from '@/api/queries/useExternalContacts';
import {
  useCreateExternalContact,
  useUpdateExternalContact,
  useDeleteExternalContact
} from '@/api/mutations/useExternalContacts';
import type { ApiError } from '@/lib/agent.types';
import { logger } from '@/lib/logger';
import { isValidationError, setFormValidationErrors, translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import {
  Badge,
  getColorSchemeFromString,
  renderFallback,
  createActionsColumn,
  createColumnHeader,
  ExternalContactFormDialog,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { OrganizationTabsNav } from '@/components/organization/OrganizationTabsNav';
import {
  createEntityListPage,
  type EntityListFormDialogArgs,
  type EntityListPageDescriptor
} from '@/components/entity-list/EntityListPage';

type ExternalContactData = AddExternalContactRequest | UpdateExternalContactRequest;

/**
 * ExternalContactFormDialog is not built on FormDialog, so the submit error
 * routing stays here: validation errors inline, the rest as a toast.
 */
function ExternalContactDialog({
  open,
  onOpenChange,
  entity,
  isSubmitting,
  onSubmit
}: EntityListFormDialogArgs<
  ExternalContact,
  AddExternalContactRequest,
  UpdateExternalContactRequest,
  AddExternalContactResponse
>) {
  const { t } = useTranslation();
  const { data: contacts } = useExternalContacts();
  const formSetErrorRef = useRef<UseFormSetError<ExternalContactData> | null>(null);

  const handleSubmit = async (data: ExternalContactData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      logger.error('Failed to save entity:', error);
      const apiError = error as ApiError;
      const setError = formSetErrorRef.current;
      if (isValidationError(apiError) && setError) {
        setFormValidationErrors(
          apiError.errors,
          setError as UseFormSetError<FieldValues>,
          t,
          apiError.metadata
        );
        return;
      }
      toastRequestError(apiError, translateErrorMessage(apiError, t));
    }
  };

  return (
    <ExternalContactFormDialog
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      contact={entity}
      isSubmitting={isSubmitting}
      onSetErrorReady={(setError) => {
        formSetErrorRef.current = setError;
      }}
      existingContacts={contacts}
    />
  );
}

export const externalContactsDescriptor: EntityListPageDescriptor<
  ExternalContact,
  AddExternalContactRequest,
  UpdateExternalContactRequest,
  AddExternalContactResponse
> = {
  documentTitleKey: 'pageTitles.externalContacts',
  i18nPrefix: 'organization.contacts',
  icon: ENTITY_ICONS.externalContact,
  TabsNav: OrganizationTabsNav,
  tableId: 'externalContacts',
  enableGlobalFilter: true,
  useRows: () => useExternalContacts(),
  useMutations: () => ({
    create: useCreateExternalContact(),
    update: useUpdateExternalContact(),
    remove: useDeleteExternalContact()
  }),
  useColumns: ({ rows, openEdit, openDelete }) => {
    const { t } = useTranslation();
    const slug = useAuthStore().slug();
    const navigate = useNavigate();

    const handleView = useCallback(
      (contact: ExternalContact) => {
        navigate({
          to: '/$slug/external-contacts/$externalContactId',
          params: { slug, externalContactId: contact._id }
        });
      },
      [navigate, slug]
    );

    const typeFilterOptions = useMemo(() => {
      const uniqueTypes = [...new Set(rows.map((c) => c.type).filter(Boolean))];
      return uniqueTypes.sort().map((type) => ({ label: type, value: type }));
    }, [rows]);

    return useMemo<DataTableColumnDef<ExternalContact>[]>(
      () => [
        {
          accessorKey: 'name',
          meta: { label: t('common.Name'), filterType: 'text', sticky: 'left' },
          header: createColumnHeader(t('common.Name')),
          cell: ({ row }) => (
            <Link
              to="/$slug/external-contacts/$externalContactId"
              params={{ slug, externalContactId: row.original._id }}
              className="font-medium hover:underline"
            >
              {row.original.name}
            </Link>
          ),
          enableColumnFilter: true
        },
        {
          accessorKey: 'email',
          meta: { label: t('common.Email'), filterType: 'text' },
          header: createColumnHeader(t('common.Email')),
          cell: ({ row }) => renderFallback(row.getValue('email')),
          enableColumnFilter: true
        },
        {
          accessorKey: 'type',
          meta: {
            label: t('common.Type'),
            filterType: 'select',
            filterOptions: typeFilterOptions
          },
          header: createColumnHeader(t('common.Type')),
          cell: ({ row }) => {
            const type = row.getValue('type') as string | undefined;
            return type ? (
              <Badge shape="rounded" colorScheme={getColorSchemeFromString(type)}>
                {type}
              </Badge>
            ) : (
              renderFallback(type)
            );
          },
          enableColumnFilter: true
        },
        {
          accessorKey: 'description',
          meta: { label: t('common.Description'), filterType: 'text' },
          header: createColumnHeader(t('common.Description')),
          cell: ({ row }) => renderFallback(row.getValue('description')),
          enableColumnFilter: true
        },
        createActionsColumn<ExternalContact>({
          onEdit: openEdit,
          onDelete: (contact) => openDelete(contact._id),
          onView: handleView
        })
      ],
      [t, slug, handleView, typeFilterOptions, openEdit, openDelete]
    );
  },
  renderFormDialog: (args) => <ExternalContactDialog {...args} />,
  deleteEntityPhraseKey: 'organization.contacts.deletePhrase'
};

export const ExternalContactsListPage = createEntityListPage(externalContactsDescriptor);
